import i18n from "@/config/i18n";
import {
  getMessageByServerIdFromLocalDB,
  updateMessageByServerIdInLocalDB,
} from "@/indexedDB/services/messages";
import { ReactionSendingStatus, UserReaction } from "@/indexedDB/types";
import {
  umbra_deleteMessageReaction,
  umbra_postMessageReaction,
} from "@/server/services/umbra/reactionService";
import { AddReaction, DeleteReaction } from "@/server/types/umbra/reaction";

export const umbra_addReactionToMessage = async ({
  chatId,
  messageId,
  emoji,
}: AddReaction): Promise<void> => {
  let prev_userReaction: UserReaction | undefined = undefined;
  let prev_reactionCounts: Record<string, number> = {};

  try {
    // store current user reaction state
    const message = await getMessageByServerIdFromLocalDB(messageId);
    if (!message) {
      throw new Error(i18n.t("errorOccurred"));
    }

    prev_userReaction = message.userReaction;
    prev_reactionCounts = { ...message.reactions };

    // if the user has already reacted, do nothing
    if (prev_userReaction) {
      // TO DO: maybe toast a message that user has already reacted
      return;
    }

    // OPTIMISTIC UPDATE - happens BEFORE API call
    const newCounts = { ...prev_reactionCounts };
    newCounts[emoji] = (newCounts[emoji] || 0) + 1;

    const new_userReaction: UserReaction = {
      content: emoji,
      messageId,
      sendingStatus: ReactionSendingStatus.PENDING,
    };

    await updateMessageByServerIdInLocalDB(messageId, {
      userReaction: new_userReaction,
      reactions: newCounts,
    });

    // post reaction to server
    await umbra_postMessageReaction({
      chatId,
      messageId,
      emoji,
    });

    // OPTIMISTIC UPDATE - happens AFTER API call
    // mark as success
    await updateMessageByServerIdInLocalDB(messageId, {
      userReaction: {
        ...new_userReaction,
        sendingStatus: ReactionSendingStatus.SUCCESS,
      },
    });

    // Note: Socket will send the real counts, which will override this
  } catch (error) {
    // rollback user reaction state in case of error

    // Restore previous reaction state
    await updateMessageByServerIdInLocalDB(messageId, {
      userReaction: prev_userReaction,
      reactions: prev_reactionCounts,
    });

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error(i18n.t("errorOccurred"));
  }
};

export const umbra_removeReactionFromMessage = async ({
  chatId,
  messageId,
  emoji,
}: DeleteReaction): Promise<void> => {
  let prev_userReaction: UserReaction | undefined = undefined;
  let prev_reactionCounts: Record<string, number> = {};

  try {
    // store current user reaction state
    const message = await getMessageByServerIdFromLocalDB(messageId);
    if (!message) {
      throw new Error(i18n.t("errorOccurred"));
    }

    prev_userReaction = message.userReaction;
    prev_reactionCounts = { ...message.reactions };

    // if reaction was not successfully sent, do nothing
    if (prev_userReaction?.sendingStatus !== ReactionSendingStatus.SUCCESS) {
      return;
    }

    // OPTIMISTIC UPDATE - happens BEFORE API call
    const newCounts = { ...prev_reactionCounts };
    if (newCounts[emoji]) {
      newCounts[emoji] -= 1;
      if (newCounts[emoji] <= 0) {
        delete newCounts[emoji]; // Remove emoji if count is 0
      }
    }

    await updateMessageByServerIdInLocalDB(messageId, {
      userReaction: undefined,
      reactions: newCounts,
    });

    // API call
    await umbra_deleteMessageReaction({
      chatId,
      messageId,
      emoji,
    });
    // Note: Socket will send the real counts, which will override this
  } catch (error) {
    // Restore previous reaction state
    await updateMessageByServerIdInLocalDB(messageId, {
      userReaction: prev_userReaction,
      reactions: prev_reactionCounts,
    });

    if (error instanceof Error && error.message) {
      throw error;
    }

    throw new Error(i18n.t("errorOccurred"));
  }
};
